/**
 * 主状态面板的文案：只描述已知事实，不替 TA 编内容。
 * “去打工的路上”和“在打工”严格分开；没有时间就不写“还要几分钟”。
 * 标题同时给按语义切开的几段（headlineParts，拼起来就是 headline）：面板在段与段之间放 <wbr>，配合 keep-all 只在这些地方折行
 * （“{名字}在{地点}”断在“在”后面，其余句式按各自的语义断；见 world-map.css 的 .ps-wmap-panel h2）。
 * 时长和旅途页同一种写法（巡检 R9-10：面板原来写“到家还要 537 分钟”，旅途页写“还要 8 小时 56 分”）：共用 shared/time/clock 的 formatDuration，
 * 到 60 分钟就写“X 小时 Y 分”；数字和单位之间是不断行空格（全站规矩，同旅行计划、驾校），星币也一样。
 */
import { formatDuration } from "@/shared/time/clock";
import { estimateLeftText } from "@/features/journey/factTime";
import type { WorldPet } from "./model";

const HHMM = new Intl.DateTimeFormat("zh-CN", { hour: "2-digit", minute: "2-digit", hour12: false });

/** 几点几分（看的人所在时区）。 */
export function clockText(ms: number): string {
  return HHMM.format(ms);
}

export interface PanelCopy {
  /** 一句话：谁在哪、在做什么。 */
  headline: string;
  /** 同一句话按语义切开的几段，拼起来就是 headline（段与段之间是折行点；读屏念的仍是整句）。 */
  headlineParts: readonly string[];
  /** 第二行：地点 / 时间 / 工钱等补充。 */
  detail: string | null;
  /** 路上的进度（0–1）；不在路上为 null。 */
  progress: number | null;
}

/** 按语义分段说一句：整句就是几段拼起来，不会两边对不上。 */
function said(...parts: string[]): Pick<PanelCopy, "headline" | "headlineParts"> {
  return { headline: parts.join(""), headlineParts: parts };
}

/** 不断行空格（U+00A0）：数字和紧跟的单位粘在一起，窄屏不会折成“28 / 星币”。时长由 shared/time/clock 的 formatDuration 自己粘好（2026-09-25 起）；几段之间“·”前面也用它（joinParts）；“工钱 N 星币”“几点 收工”“还要 N”“待了 N”的标签和数之间也用它，窄屏不会折成“工钱 / 25 星币”。 */
const NBSP = String.fromCharCode(0xa0);

function coinsText(pay: number): string {
  return `${pay}${NBSP}星币`;
}

/**
 * 还要多久：和旅途页、行程卡同一个说法、同一个函数（journey/factTime 的 estimateLeftText，I 10-13 统一；原来这里剩 1 分钟以内说“马上就到”）；没有时间就不写。
 * 面板窄，“预计还要”和时长之间照旧用不断行空格粘住（上面 NBSP 的规矩，窄屏不折成“预计还要 / 55 分钟”）；字一个不改，只管折行。
 */
function leftText(until: number | null, nowMs: number, target?: string): string | null {
  return until == null ? null : estimateLeftText(until - nowMs, target).replace("预计还要 ", `预计还要${NBSP}`);
}

/** 几段之间用“ · ”连，“·”前面是不断行空格：“·”粘在上一段末尾，窄屏不会跑到行首（面板样式 keep-all 只在普通空格处折行，见 world-map.css 的 .ps-wmap-panel p）。 */
function joinParts(parts: Array<string | null | undefined>): string | null {
  const kept = parts.filter((p): p is string => Boolean(p && p.trim()));
  return kept.length ? kept.join(`${NBSP}· `) : null;
}

/** 已有地点或正在做的事说过住处名字时，不重复说；不从夜间信号推测住宿或睡眠。 */
function stayText(pet: WorldPet): string | null {
  const name = pet.activity.stayPlace?.name.trim();
  if (!name || pet.activity.place?.name.trim() === name || pet.activity.doing?.includes(name)) return null;
  return `住处：${name}`;
}

export function panelCopy(pet: WorldPet, nowMs: number, progress: number | null): PanelCopy {
  const { name } = pet;
  const a = pet.activity;
  switch (a.phase) {
    case "home": {
      // 在家时按结构化姿态说一句（后端只在事实确定时给 sleeping 等；idle 就只说在小窝里）。断点：“在”后面、做的事前面。
      const headline =
        pet.activity.pose === "sleeping"
          ? said(`${name}在`, "小窝里", "睡觉")
          : pet.activity.pose === "eating"
            ? said(`${name}在`, "小窝里", "吃东西")
            : pet.activity.pose === "sunbathing"
              ? said(`${name}在`, "院子里", "晒太阳")
              : said(`${name}在`, "小窝里");
      return { ...headline, detail: a.doing ?? (pet.home ? `家在${pet.home.label}` : null), progress: null };
    }
    case "going":
      if (a.kind === "job") {
        return {
          ...said(`${name}在`, "去打工的路上"),
          detail: joinParts([a.place ? `去${a.place.name}` : null, a.job?.title, leftText(a.until, nowMs)]),
          progress,
        };
      }
      return {
        ...(a.kind === "stroll" ? said(name, "出门走走") : a.kind === "cafe" ? said(name, "去喝一杯") : said(name, "出门了")),
        detail: joinParts([a.place ? `往${a.place.name}去` : a.title, leftText(a.until, nowMs)]),
        progress,
      };
    case "there": {
      // 实际睡眠只认pose，overnight只是时段。到期或跨日也不能替服务器改成返家。
      const sleeping = a.pose === "sleeping"
        ? (a.place ? said(`${name}在`, a.place.name, "睡觉") : said(name, "在外面睡觉"))
        : null;
      const stay = stayText(pet);
      if (a.kind === "job") {
        return {
          ...(sleeping ?? said(`${name}在`, a.place?.name ?? "干活的地方", "打工")),
          detail: joinParts([a.doing ?? a.job?.title, stay, a.until != null ? `${clockText(a.until)}${NBSP}收工` : null, a.job ? `工钱${NBSP}${coinsText(a.job.pay)}，收工后到账` : null]),
          progress: null,
        };
      }
      return {
        ...(sleeping ?? (a.place ? said(`${name}在`, a.place.name) : said(name, "到地方了"))),
        // 待了多久按整分钟往下取（至少 1 分钟），再用同一个时长写法：待满 60 分钟写“1 小时”。
        detail: joinParts([a.doing, stay, a.since != null ? `待了${NBSP}${formatDuration(Math.max(1, Math.floor((nowMs - a.since) / 60_000)) * 60_000)}` : null]),
        progress: null,
      };
    }
    case "returning":
      return {
        ...(a.kind === "job" ? said(name, "收工了，", "在回家的路上") : said(`${name}在`, "回家的路上")),
        detail: joinParts([
          leftText(a.until, nowMs, "到家"),
          a.kind === "job" && a.job ? (a.job.paid ? `工钱${NBSP}${coinsText(a.job.pay)}已进银行卡` : `工钱${NBSP}${coinsText(a.job.pay)}稍后到账`) : null,
        ]),
        progress,
      };
    default:
      return { ...said("还没同步到", `${name}此刻在哪`), detail: "世界状态更新后，这里会跟着变。", progress: null };
  }
}
